import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { 
  Copy, 
  ThumbsUp, 
  ThumbsDown, 
  BarChart3, 
  ChevronDown, 
  ChevronRight,
  Info,
  Clock,
  Cpu,
  Lightbulb
} from 'lucide-react';
import { cn } from '../../lib/utils';

interface AnalyticsMessageProps {
  message: {
    id: string;
    content: string;
    timestamp: string;
    visualizations?: any[];
    followUpQuestions?: string[];
    processingTime?: number;
    tokensUsed?: number;
    queryDetails?: {
      metrics?: string[];
      dimensions?: string[];
      dateRange?: { startDate: string; endDate: string } | { preset: string } | string;
      filters?: any[];
      orderBy?: any[];
      limit?: number;
      endpoint?: string;
      cached?: boolean;
      aiAnalytics?: boolean;
      processingTime?: number;
    };
    structuredData?: {
      title?: string;
      summary?: string;
      keyMetrics?: Array<{
        label: string;
        value: string;
        change?: string;
        trend?: 'up' | 'down' | 'stable' | 'unknown';
      }>;
      insights?: string[];
      businessContext?: string[];
      dataTable?: {
        headers: string[];
        rows: string[][];
        title: string;
      };
      queryInfo?: {
        dateRange?: string;
        dataPoints?: number;
        primaryMetrics?: string[];
      };
      followUpQuestions?: string[];
      // Multi-dimensional analysis fields
      relationshipAnalysis?: {
        discoveredRelationships: Array<{
          type: string;
          description: string;
          strength: 'strong' | 'moderate' | 'weak';
          businessImpact: string;
        }>;
        crossDimensionalInsights: string[];
        dataConnections: Array<{
          source: string;
          target: string;
          relationship: string;
          significance: string;
        }>;
      };
      analysisComplexity?: {
        level: 'simple' | 'moderate' | 'complex' | 'multi-dimensional';
        description: string;
        queryCount: number;
        processingStrategy: string;
        aiEnhanced: boolean;
      };
      // Event clarification specific fields
      suggestedEvents?: Array<{
        eventName: string;
        description: string;
        example: string;
        isFromGA4?: boolean;
        similarity?: number;
      }>;
      helpText?: string;
      hasRealEvents?: boolean;
      totalEventsFound?: number;
      // Playbook specific fields
      playbook?: {
        title: string;
        summary: string;
        steps?: Array<{
          title: string;
          description: string;
          code?: string;
          tips?: string[];
        }>;
        bestPractices?: string[];
        commonMistakes?: string[];
        nextSteps?: string[];
        resources?: Array<{
          title: string;
          url: string;
          description: string;
        }>;
      };
      // Marketing Strategy specific fields
      responseType?: string;
      executiveSummary?: string;
      strategicRecommendations?: Array<{
        title: string;
        description: string;
        impact: 'high' | 'medium' | 'low';
        effort: 'high' | 'medium' | 'low';
        timeframe: string;
        contextualNote?: string;
      }>;
      actionableSteps?: Array<{
        step: string;
        description: string;
        owner: string;
        timeline: string;
        priority: 'high' | 'medium' | 'low';
      }>;
      metricsToTrack?: Array<{
        metric: string;
        currentValue: string;
        targetValue: string;
        trackingFrequency: string;
      }>;
      riskFactors?: Array<{
        risk: string;
        impact: string;
        mitigation: string;
      }>;
      bestPractices?: string[];
      commonMistakes?: string[];
    };
    metadata?: {
      clarificationType?: string;
      retryInfo?: {
        queriesRequiredRetry: number;
        totalQueries: number;
        retryStrategiesUsed: string[];
        fallbacksUsed: number;
        maxRetryLayer: number;
      };
    };
  };
  onFollowUpQuestion?: (question: string) => void;
}

export function AnalyticsMessage({ message, onFollowUpQuestion }: AnalyticsMessageProps) {
  const [showQueryDetails, setShowQueryDetails] = useState(false);

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
  };

  // Utility function to format text with bold patterns (inline)
  const formatTextWithBold = (text: string) => {
    // Split by **text** pattern and create JSX elements
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        // Remove ** and make bold
        const boldText = part.slice(2, -2);
        return <strong key={index} className="font-semibold text-gray-900 dark:text-white">{boldText}</strong>;
      }
      return part;
    });
  };

  // Utility function to format multiline text with markdown headers and bold patterns
  const formatMultilineContent = (text: string) => {
    // Split text into lines to handle headers properly
    const lines = text.split('\n');
    
    return lines.map((line, lineIndex) => {
      // Handle code blocks
      if (line.startsWith('```')) {
        const nextCodeBlock = lines.findIndex((l, i) => i > lineIndex && l.startsWith('```'));
        if (nextCodeBlock !== -1) {
          const codeLines = lines.slice(lineIndex + 1, nextCodeBlock);
          const codeContent = codeLines.join('\n');
          return (
            <div key={lineIndex} className="mb-4">
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                <code>{codeContent}</code>
              </pre>
            </div>
          );
        }
      }
      
      // Skip lines that are part of code blocks (handled above)
      if (line.startsWith('```')) {
        return null;
      }
      
      // Handle markdown headers
      if (line.startsWith('### ')) {
        return (
          <h3 key={lineIndex} className="text-lg font-semibold text-gray-900 dark:text-white mt-4 mb-2 text-left">
            {line.slice(4)}
          </h3>
        );
      }
      if (line.startsWith('## ')) {
        return (
          <h2 key={lineIndex} className="text-xl font-bold text-gray-900 dark:text-white mt-6 mb-3 text-left">
            {line.slice(3)}
          </h2>
        );
      }
      if (line.startsWith('# ')) {
        return (
          <h1 key={lineIndex} className="text-2xl font-bold text-gray-900 dark:text-white mt-6 mb-4 text-left">
            {line.slice(2)}
          </h1>
        );
      }
      
      // Handle empty lines
      if (line.trim() === '') {
        return <div key={lineIndex} className="h-2"></div>;
      }
      
      // Handle bullet points
      if (line.startsWith('- ') || line.startsWith('• ')) {
        return (
          <div key={lineIndex} className="flex items-start space-x-2 mb-2">
            <span className="text-blue-500 dark:text-blue-400 mt-1">•</span>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{formatTextWithBold(line.slice(2))}</p>
          </div>
        );
      }
      
      // Regular paragraphs
      return (
        <p key={lineIndex} className="text-gray-700 dark:text-gray-300 mb-2 text-left leading-relaxed">
          {formatTextWithBold(line)}
        </p>
      );
    }).filter(Boolean); // Remove null entries from code block handling
  };

  const parseAnalyticsContent = (content: string) => {
    const sections = {
      title: '',
      keyMetrics: [] as Array<{ label: string; value: string }>,
      insights: [] as string[],
      businessContext: [] as string[],
      performanceMetrics: [] as Array<{ label: string; value: string }>
    };

    // Debug: log the content to see what we're parsing
    console.log('AnalyticsMessage - Parsing content:', content);

    const lines = content.split('\n');
    let currentSection = '';

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      // Skip empty lines
      if (!trimmedLine) return;
      
      // Extract title from first significant line or h1/h2/h3 headers
      if (!sections.title && (trimmedLine.startsWith('# ') || trimmedLine.startsWith('## ') || trimmedLine.startsWith('### ') || 
          (!trimmedLine.startsWith('•') && !trimmedLine.startsWith('-') && !trimmedLine.startsWith('**') && index < 5))) {
        sections.title = trimmedLine.replace(/^#+\s*/, '');
        return;
      }

      // Section headers - more flexible matching
      if (trimmedLine.includes('#### GA4 Query') || trimmedLine.includes('Query Executed') || trimmedLine.includes('Execution Details')) {
        currentSection = 'skip'; // Skip query execution details
        return;
      }
      
      if (trimmedLine.includes('#### Performance') || trimmedLine.includes('Overall Performance') || trimmedLine.includes('Performance Metrics') || 
          trimmedLine.includes('Performance Overview')) {
        currentSection = 'performanceMetrics';
        return;
      }
      
      if (trimmedLine.includes('#### Analysis') || trimmedLine.includes('Key Insights') || trimmedLine.includes('Analysis') ||
          trimmedLine.includes('### Insights') || trimmedLine.includes('### Analysis')) {
        currentSection = 'insights';
        return;
      }
      
      if (trimmedLine.includes('#### Business Context') || trimmedLine.includes('Business Context') || 
          trimmedLine.includes('Recommendations') || trimmedLine.includes('Context')) {
        currentSection = 'businessContext';
        return;
      }

      // Handle numbered lists that might contain traffic sources
      if (/^\d+\.\s/.test(trimmedLine)) {
        const cleanLine = trimmedLine.replace(/^\d+\.\s*/, '');
        
        if (cleanLine.includes(':')) {
          const colonIndex = cleanLine.indexOf(':');
          const label = cleanLine.substring(0, colonIndex).trim();
          const value = cleanLine.substring(colonIndex + 1).trim();
          
          // Check if this looks like a traffic source or metric
          if (/\d/.test(value) || value.includes('%') || value.includes('$') || value.includes('sessions')) {
            sections.keyMetrics.push({ label, value });
          }
        } else {
          // Handle numbered items without colons
          if (currentSection === 'insights') {
            sections.insights.push(cleanLine);
          } else {
            sections.keyMetrics.push({ label: `Item ${index}`, value: cleanLine });
          }
        }
        return;
      }

      // Look for patterns like "**Total Sessions**: 12,784 sessions"
      if (trimmedLine.includes('**') && trimmedLine.includes(':')) {
        const cleanLine = trimmedLine.replace(/\*\*/g, '');
        const colonIndex = cleanLine.indexOf(':');
        if (colonIndex > 0) {
          const label = cleanLine.substring(0, colonIndex).trim();
          const value = cleanLine.substring(colonIndex + 1).trim();
          
          if (/\d/.test(value) || value.includes('%') || value.includes('$')) {
            sections.keyMetrics.push({ label, value });
          }
        }
        return;
      }

      if (trimmedLine.includes('Suggested Follow-Up') || trimmedLine.includes('Would you like')) {
        currentSection = 'skip'; // Skip follow-up sections
        return;
      }

      // Handle bullet points and list items
      if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-')) {
        const cleanLine = trimmedLine.replace(/^[•-]\s*/, '');
        
        // Look for key-value pairs with various patterns
        if (cleanLine.includes(':')) {
          // Handle patterns like "Active Users: 132", "Event Count: 206"
          const colonIndex = cleanLine.indexOf(':');
          const label = cleanLine.substring(0, colonIndex).trim();
          const value = cleanLine.substring(colonIndex + 1).trim();
          
          // Check if this looks like a metric (numbers, percentages, etc.)
          if (/\d/.test(value) || value.includes('%') || value.includes('$')) {
            if (label.includes('Users') || label.includes('Count') || label.includes('Rate') || 
                label.includes('Total') || label.includes('Event') || label.includes('Sessions')) {
              if (currentSection === 'performanceMetrics' || !currentSection) {
                sections.keyMetrics.push({ label, value });
              } else {
                sections.performanceMetrics.push({ label, value });
              }
            } else {
              sections.performanceMetrics.push({ label, value });
            }
          } else {
            // Non-metric content goes to appropriate section
            if (currentSection === 'insights') {
              sections.insights.push(cleanLine);
            } else if (currentSection === 'businessContext') {
              sections.businessContext.push(cleanLine);
            } else if (currentSection === 'performanceMetrics') {
              sections.performanceMetrics.push({ label, value });
            }
          }
        } else {
          // Regular bullet point without colon
          if (currentSection === 'insights') {
            sections.insights.push(cleanLine);
          } else if (currentSection === 'businessContext') {
            sections.businessContext.push(cleanLine);
          }
        }
      } 
      // Handle indented sub-bullets like "- Active Users: 132"
      else if (trimmedLine.startsWith('- **') || (trimmedLine.startsWith('- ') && trimmedLine.includes(':'))) {
        const cleanLine = trimmedLine.replace(/^-\s*\*\*/, '').replace(/\*\*/, '').trim();
        
        if (cleanLine.includes(':')) {
          const colonIndex = cleanLine.indexOf(':');
          const label = cleanLine.substring(0, colonIndex).trim();
          const value = cleanLine.substring(colonIndex + 1).trim();
          
          if (/\d/.test(value) || value.includes('%') || value.includes('$')) {
            sections.keyMetrics.push({ label, value });
          }
        }
      }
      // Handle regular paragraphs
      else if (!trimmedLine.startsWith('#') && currentSection && currentSection !== 'skip') {
        if (currentSection === 'insights') {
          sections.insights.push(trimmedLine);
        } else if (currentSection === 'businessContext') {
          sections.businessContext.push(trimmedLine);
        }
      }
    });

    console.log('AnalyticsMessage - Parsed sections:', sections);
    return sections;
  };

  // Debug: Log what we're receiving
  console.log('AnalyticsMessage - Message received:', {
    hasContent: !!message.content,
    contentLength: message.content?.length || 0,
    contentPreview: message.content?.substring(0, 200),
    hasStructuredData: !!message.structuredData,
    structuredDataKeys: message.structuredData ? Object.keys(message.structuredData) : [],
    hasInsights: !!message.structuredData?.insights,
    insightsCount: message.structuredData?.insights?.length || 0,
    hasBusinessContext: !!message.structuredData?.businessContext,
    businessContextCount: message.structuredData?.businessContext?.length || 0
  });

  // Use structured data if available, otherwise parse content
  const structuredData = message.structuredData;
  const sections = structuredData ? null : parseAnalyticsContent(message.content);
  
  // Check if structured data has meaningful content
  const hasStructuredContent = structuredData && (
    (structuredData.insights && structuredData.insights.length > 0) ||
    (structuredData.businessContext && structuredData.businessContext.length > 0) ||
    (structuredData.keyMetrics && structuredData.keyMetrics.length > 0) ||
    (structuredData.summary && structuredData.summary.trim().length > 0) ||
    (structuredData.playbook && structuredData.playbook.title) ||
    (structuredData.executiveSummary && structuredData.executiveSummary.trim().length > 0) ||
    (structuredData.strategicRecommendations && structuredData.strategicRecommendations.length > 0) ||
    (structuredData.actionableSteps && structuredData.actionableSteps.length > 0) ||
    (structuredData.metricsToTrack && structuredData.metricsToTrack.length > 0)
  );

  return (
    <div className="flex justify-start">
      <div className="w-full max-w-[95%] sm:max-w-4xl mobile-rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 ml-2 mr-4 sm:mr-12 shadow-sm">
        {/* Message Header - Mobile Optimized */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 border-b border-gray-100 dark:border-gray-700 space-y-2 sm:space-y-0">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
              <BarChart3 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <span className="text-sm font-medium text-gray-900 dark:text-white">Analytics Agent</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">{formatTime(message.timestamp)}</span>
          </div>
          
          <div className="flex items-center flex-wrap gap-2">
            {/* Analysis Complexity Indicator */}
            {message.structuredData?.analysisComplexity && (
              <Badge 
                variant={message.structuredData.analysisComplexity.level === 'multi-dimensional' ? 'default' : 'secondary'} 
                className={cn(
                  "text-xs",
                  message.structuredData.analysisComplexity.level === 'multi-dimensional' && "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-700",
                  message.structuredData.analysisComplexity.aiEnhanced && "border-blue-300 dark:border-blue-600"
                )}
              >
                {message.structuredData.analysisComplexity.aiEnhanced && <Lightbulb className="w-3 h-3 mr-1" />}
                {message.structuredData.analysisComplexity.level === 'multi-dimensional' ? 'Multi-Dimensional' : 
                 message.structuredData.analysisComplexity.level === 'complex' ? 'Complex' :
                 message.structuredData.analysisComplexity.level === 'moderate' ? 'Moderate' : 'Simple'}
                <span className="hidden sm:inline">
                  {message.structuredData.analysisComplexity.queryCount > 1 && ` (${message.structuredData.analysisComplexity.queryCount} queries)`}
                </span>
              </Badge>
            )}
            {message.processingTime && (
              <Badge variant="secondary" className="text-xs">
                <Clock className="w-3 h-3 mr-1" />
                <span className="hidden sm:inline">{(message.processingTime / 1000).toFixed(1)}s</span>
                <span className="sm:hidden">{(message.processingTime / 1000).toFixed(1)}s</span>
              </Badge>
            )}
            {message.tokensUsed && (
              <Badge variant="secondary" className="text-xs">
                <Cpu className="w-3 h-3 mr-1" />
                <span className="hidden sm:inline">{message.tokensUsed.toLocaleString()}</span>
                <span className="sm:hidden">{message.tokensUsed > 1000 ? `${Math.round(message.tokensUsed/1000)}k` : message.tokensUsed}</span>
              </Badge>
            )}
          </div>
        </div>

        <div className="p-3 sm:p-6">
          {/* Structured Data Display */}
          {structuredData && hasStructuredContent ? (
            <>
              {/* Title */}
              {structuredData.title && (
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4 leading-relaxed">
                  {structuredData.title}
                </h2>
              )}

              {/* Summary */}
              {structuredData.summary && (
                <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 mobile-rounded border-l-4 border-blue-500 dark:border-blue-400">
                  <p className="text-gray-800 dark:text-gray-200 leading-relaxed text-sm sm:text-base">{structuredData.summary}</p>
                </div>
              )}

              {/* Key Metrics - Mobile First Grid */}
              {structuredData.keyMetrics && structuredData.keyMetrics.length > 0 && (
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-3">Key Metrics</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {structuredData.keyMetrics.map((metric, index) => (
                      <div key={index} className="bg-blue-50 dark:bg-blue-900/20 mobile-rounded p-3 sm:p-4">
                        <div className="text-xs sm:text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">{metric.label}</div>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 space-y-1 sm:space-y-0">
                          <div className="text-lg sm:text-2xl font-bold text-blue-900 dark:text-blue-100">{metric.value}</div>
                          {metric.change && (
                            <span className={cn(
                              "text-xs sm:text-sm font-medium px-2 py-1 rounded-full w-fit",
                              metric.trend === 'up' ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300" :
                              metric.trend === 'down' ? "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300" :
                              "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                            )}>
                              {metric.change}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Insights - Mobile Optimized */}
              {structuredData.insights && structuredData.insights.length > 0 && (
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-3">📊 Key Insights</h3>
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 mobile-rounded p-3 sm:p-4">
                    <div className="space-y-2 sm:space-y-3">
                      {structuredData.insights.map((insight, index) => (
                        <div key={index} className="flex items-start space-x-2 sm:space-x-3">
                          <div className="w-1.5 h-1.5 bg-green-500 dark:bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                          <p className="text-green-800 dark:text-green-200 leading-relaxed text-sm sm:text-base">{insight}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Analysis Complexity - Mobile Layout */}
              {structuredData.analysisComplexity && (
                <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 mobile-rounded">
                  <div className="flex flex-col sm:flex-row sm:items-start space-y-2 sm:space-y-0 sm:space-x-3">
                    <Lightbulb className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="text-sm sm:text-base font-semibold text-purple-800 dark:text-purple-200 mb-1">Analysis Complexity</h4>
                      <p className="text-purple-700 dark:text-purple-300 text-sm leading-relaxed mb-2">{structuredData.analysisComplexity.description}</p>
                      <div className="flex flex-wrap gap-2 text-xs">
                        <span className="bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-200 px-2 py-1 rounded-full">
                          {structuredData.analysisComplexity.level}
                        </span>
                        <span className="bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-200 px-2 py-1 rounded-full">
                          {structuredData.analysisComplexity.queryCount} queries
                        </span>
                        {structuredData.analysisComplexity.aiEnhanced && (
                          <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full">
                            AI Enhanced
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Relationship Analysis - Mobile Grid */}
              {structuredData.relationshipAnalysis && (
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-3">🔗 Relationship Analysis</h3>
                  <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 mobile-rounded p-3 sm:p-4">
                    {/* Discovered Relationships - Mobile Optimized */}
                    {structuredData.relationshipAnalysis.discoveredRelationships && structuredData.relationshipAnalysis.discoveredRelationships.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm sm:text-base font-semibold text-purple-800 dark:text-purple-200 mb-3">Discovered Relationships</h4>
                        <div className="space-y-3">
                          {structuredData.relationshipAnalysis.discoveredRelationships.map((rel, index) => (
                            <div key={index} className="bg-white dark:bg-gray-800 border border-purple-200 dark:border-purple-700 mobile-rounded p-3">
                              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2">
                                <h5 className="font-medium text-gray-900 dark:text-white text-sm">{rel.type}</h5>
                                <span className={cn(
                                  "px-2 py-1 rounded-full text-xs font-medium w-fit mt-1 sm:mt-0",
                                  rel.strength === 'strong' ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300" :
                                  rel.strength === 'moderate' ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300" :
                                  "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                                )}>
                                  {rel.strength}
                                </span>
                              </div>
                              <p className="text-purple-700 dark:text-purple-300 text-sm mb-2 leading-relaxed">{rel.description}</p>
                              <p className="text-purple-600 dark:text-purple-400 text-xs">
                                <strong>Business Impact:</strong> {rel.businessImpact}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Cross-Dimensional Insights */}
                    {structuredData.relationshipAnalysis.crossDimensionalInsights && structuredData.relationshipAnalysis.crossDimensionalInsights.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm sm:text-base font-semibold text-purple-800 dark:text-purple-200 mb-3">Cross-Dimensional Insights</h4>
                        <div className="space-y-2">
                          {structuredData.relationshipAnalysis.crossDimensionalInsights.map((insight, index) => (
                            <div key={index} className="flex items-start space-x-3">
                              <div className="w-2 h-2 bg-purple-500 dark:bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                              <p className="text-purple-800 dark:text-purple-200 leading-relaxed text-sm">{insight}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Data Connections - Mobile Grid */}
                    {structuredData.relationshipAnalysis.dataConnections && structuredData.relationshipAnalysis.dataConnections.length > 0 && (
                      <div>
                        <h4 className="text-sm sm:text-base font-semibold text-purple-800 dark:text-purple-200 mb-3">Data Connections</h4>
                        <div className="grid grid-cols-1 gap-3">
                          {structuredData.relationshipAnalysis.dataConnections.map((connection, index) => (
                            <div key={index} className="bg-white dark:bg-gray-800 border border-purple-200 dark:border-purple-700 mobile-rounded p-3">
                              <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2 text-sm">
                                <span className="font-medium text-gray-900 dark:text-white">{connection.source}</span>
                                <span className="text-purple-500 dark:text-purple-400 hidden sm:inline">→</span>
                                <span className="text-purple-500 dark:text-purple-400 sm:hidden">↓</span>
                                <span className="font-medium text-gray-900 dark:text-white">{connection.target}</span>
                              </div>
                              <p className="text-purple-700 dark:text-purple-300 text-xs mt-1">{connection.relationship}</p>
                              <p className="text-purple-600 dark:text-purple-400 text-xs mt-1">
                                <strong>Significance:</strong> {connection.significance}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Business Context */}
              {structuredData.businessContext && structuredData.businessContext.length > 0 && (
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-3">💼 Business Context & Recommendations</h3>
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 mobile-rounded p-3 sm:p-4">
                    <div className="space-y-2 sm:space-y-3">
                      {structuredData.businessContext.map((context, index) => (
                        <div key={index} className="flex items-start space-x-2 sm:space-x-3">
                          <div className="w-1.5 h-1.5 bg-amber-500 dark:bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                          <p className="text-amber-800 dark:text-amber-200 leading-relaxed text-sm sm:text-base">{context}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Data Table - Mobile Responsive */}
              {structuredData.dataTable && (
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-3">
                    {structuredData.dataTable.title || 'Detailed Data'}
                  </h3>
                  <div className="overflow-x-auto -mx-3 sm:mx-0">
                    <div className="inline-block min-w-full align-middle">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 mobile-rounded">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                          <tr>
                            {structuredData.dataTable.headers.map((header, index) => (
                              <th key={index} className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {structuredData.dataTable.rows.map((row, rowIndex) => (
                            <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}>
                              {row.map((cell, cellIndex) => (
                                <td key={cellIndex} className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-900 dark:text-gray-100">
                                  <div className="break-words">{cell}</div>
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Query Info - Mobile Layout */}
              {structuredData.queryInfo && (
                <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 mobile-rounded">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Analysis Details</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    {structuredData.queryInfo.dateRange && (
                      <div>
                        <span className="font-medium">Date Range:</span> 
                        <div className="sm:inline sm:ml-1 mt-1 sm:mt-0 break-words">{structuredData.queryInfo.dateRange}</div>
                      </div>
                    )}
                    {structuredData.queryInfo.dataPoints && (
                      <div>
                        <span className="font-medium">Data Points:</span> {structuredData.queryInfo.dataPoints.toLocaleString()}
                      </div>
                    )}
                    {structuredData.queryInfo.primaryMetrics && (
                      <div>
                        <span className="font-medium">Metrics:</span>
                        <div className="sm:inline sm:ml-1 mt-1 sm:mt-0 break-words">{structuredData.queryInfo.primaryMetrics.join(', ')}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : structuredData ? (
            <>
              {/* Fallback: Show title and data table even if no insights */}
              {structuredData.title && (
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {structuredData.title}
                </h2>
              )}

              {/* Data Table */}
              {structuredData.dataTable && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                    {structuredData.dataTable.title || 'Detailed Data'}
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600 border border-gray-200 dark:border-gray-600 rounded-lg">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          {structuredData.dataTable.headers.map((header, index) => (
                            <th key={index} className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                        {structuredData.dataTable.rows.map((row, rowIndex) => (
                          <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}>
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Query Info */}
              {structuredData.queryInfo && (
                <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Analysis Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-400">
                    {structuredData.queryInfo.dateRange && (
                      <div>
                        <span className="font-medium">Date Range:</span> {structuredData.queryInfo.dateRange}
                      </div>
                    )}
                    {structuredData.queryInfo.dataPoints && (
                      <div>
                        <span className="font-medium">Data Points:</span> {structuredData.queryInfo.dataPoints.toLocaleString()}
                      </div>
                    )}
                    {structuredData.queryInfo.primaryMetrics && (
                      <div>
                        <span className="font-medium">Metrics:</span> {structuredData.queryInfo.primaryMetrics.join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Show raw content if we have it but no structured content */}
              {message.content && message.content.trim() && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3 text-left">AI Analysis</h3>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border-l-4 border-blue-500 dark:border-blue-400">
                    <div className="max-w-none text-left text-gray-800 dark:text-gray-200">
                      {formatMultilineContent(message.content)}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Fallback: Show raw content if parsing didn't extract much and we don't have structured data */}
              {!structuredData && sections && sections.keyMetrics.length === 0 && sections.performanceMetrics.length === 0 && sections.insights.length === 0 && message.content.trim() && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3 text-left">Response</h3>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="max-w-none text-left text-gray-800 dark:text-gray-200">
                      {formatMultilineContent(message.content)}
                    </div>
                  </div>
                </div>
              )}

              {/* Always show content if we have it and no structured data (covers playbooks and other formatted content) */}
              {!hasStructuredContent && message.content && message.content.trim() && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3 text-left">
                    {message.content.includes('# ') ? 'Playbook' : 'Response'}
                  </h3>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border-l-4 border-blue-500 dark:border-blue-400">
                    <div className="max-w-none text-left text-gray-800 dark:text-gray-200">
                      {formatMultilineContent(message.content)}
                    </div>
                  </div>
                </div>
              )}

              {/* AI content sections if not structured data */}
              {sections && !hasStructuredContent && (
                <>
                  {/* Key Metrics Section */}
                  {sections.keyMetrics.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Key Metrics</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {sections.keyMetrics.map((metric, index) => (
                          <div key={index} className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                            <div className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">{formatTextWithBold(metric.label)}</div>
                            <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">{formatTextWithBold(metric.value)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Performance Metrics */}
                  {sections.performanceMetrics && sections.performanceMetrics.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Performance Overview</h3>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {sections.performanceMetrics.map((metric, index) => (
                            <div key={index} className="flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{formatTextWithBold(metric.label)}:</span>
                              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatTextWithBold(metric.value)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Insights */}
                  {sections.insights && sections.insights.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3 text-left">Key Insights</h3>
                      <div className="space-y-3">
                        {sections.insights.map((insight, index) => (
                          <div key={index} className="flex items-start space-x-3">
                            <div className="w-1.5 h-1.5 bg-blue-500 dark:bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                            <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-left">{formatTextWithBold(insight)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Business Context */}
                  {sections.businessContext && sections.businessContext.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Business Context</h3>
                      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
                        <div className="space-y-3">
                          {sections.businessContext.map((context, index) => (
                            <div key={index} className="flex items-start space-x-3">
                              <div className="w-1.5 h-1.5 bg-amber-500 dark:bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                              <p className="text-amber-800 dark:text-amber-200 leading-relaxed">{formatTextWithBold(context)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Visualizations */}
          {message.visualizations && message.visualizations.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Visualizations</h3>
              <div className="space-y-4">
                {message.visualizations.map((viz, index) => (
                  <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">{formatTextWithBold(viz.title)}</h4>
                    {viz.chartType === 'bar' && viz.data && (
                      <div className="h-64">
                        <div className="space-y-2">
                          {viz.data.slice(0, 10).map((item: any, itemIndex: number) => {
                            const maxValue = Math.max(...viz.data.map((d: any) => d.value));
                            const percentage = (item.value / maxValue) * 100;
                            return (
                              <div key={itemIndex} className="flex items-center space-x-3">
                                <div className="w-20 text-sm text-gray-600 dark:text-gray-400 truncate" title={item.label}>
                                  {item.label}
                                </div>
                                <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-4 relative">
                                  <div 
                                    className="bg-blue-500 dark:bg-blue-400 h-4 rounded-full transition-all duration-500"
                                    style={{ width: `${percentage}%` }}
                                  />
                                  <span className="absolute right-2 top-0 text-xs text-gray-700 dark:text-gray-300 leading-4">
                                    {item.value.toLocaleString()}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {viz.chartType === 'pie' && viz.data && (
                      <div className="h-64 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Pie Chart</div>
                          <div className="space-y-1">
                            {viz.data.slice(0, 5).map((item: any, itemIndex: number) => (
                              <div key={itemIndex} className="flex items-center justify-between text-sm">
                                <span className="text-gray-700 dark:text-gray-300">{item.label}:</span>
                                <span className="font-medium text-gray-900 dark:text-gray-100">{item.value.toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    {viz.chartType === 'line' && viz.data && (
                      <div className="h-64 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Time Series Chart</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {viz.data.length} data points over time
                          </div>
                        </div>
                      </div>
                    )}
                    {!viz.chartType && (
                      <div className="h-64 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center">
                        <span className="text-gray-500 dark:text-gray-400">Chart: {viz.type || 'Unknown'}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Query Details - Mobile Optimized Collapsible */}
          {message.queryDetails && (
            <div className="mb-4 sm:mb-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowQueryDetails(!showQueryDetails)}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 h-9 px-3 mobile-rounded mobile-touch-feedback"
              >
                <Info className="w-4 h-4 mr-2" />
                <span className="text-sm">Query Details</span>
                {showQueryDetails ? (
                  <ChevronDown className="w-4 h-4 ml-2" />
                ) : (
                  <ChevronRight className="w-4 h-4 ml-2" />
                )}
              </Button>
              
              {showQueryDetails && (
                <div className="mt-3 bg-gray-50 dark:bg-gray-700 mobile-rounded p-3 sm:p-4 text-sm">
                  <div className="space-y-3">
                    {/* GA4 API Endpoint */}
                    {message.queryDetails.endpoint && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">GA4 API Endpoint: </span>
                        <span className="text-gray-600 dark:text-gray-400 font-mono">{message.queryDetails.endpoint}</span>
                      </div>
                    )}
                    
                    {/* Metrics */}
                    {message.queryDetails.metrics && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Metrics: </span>
                        <span className="text-gray-600 dark:text-gray-400">{Array.isArray(message.queryDetails.metrics) ? message.queryDetails.metrics.join(', ') : message.queryDetails.metrics}</span>
                      </div>
                    )}
                    
                    {/* Dimensions */}
                    {message.queryDetails.dimensions && message.queryDetails.dimensions.length > 0 && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Dimensions: </span>
                        <span className="text-gray-600 dark:text-gray-400">{message.queryDetails.dimensions.join(', ')}</span>
                      </div>
                    )}
                    
                    {/* Date Range */}
                    {message.queryDetails.dateRange && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Date Range: </span>
                        <span className="text-gray-600 dark:text-gray-400">
                          {typeof message.queryDetails.dateRange === 'string' 
                            ? message.queryDetails.dateRange
                            : 'preset' in message.queryDetails.dateRange
                              ? message.queryDetails.dateRange.preset
                              : `${message.queryDetails.dateRange.startDate} to ${message.queryDetails.dateRange.endDate}`
                          }
                        </span>
                      </div>
                    )}
                    
                    {/* Row Limit */}
                    {message.queryDetails.limit && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Row Limit: </span>
                        <span className="text-gray-600 dark:text-gray-400">{message.queryDetails.limit.toLocaleString()}</span>
                      </div>
                    )}
                    
                    {/* Filters */}
                    {message.queryDetails.filters && message.queryDetails.filters.length > 0 && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Filters: </span>
                        <span className="text-gray-600 dark:text-gray-400">{message.queryDetails.filters.length} applied</span>
                      </div>
                    )}
                    
                    {/* Order By */}
                    {message.queryDetails.orderBy && message.queryDetails.orderBy.length > 0 && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Order By: </span>
                        <span className="text-gray-600 dark:text-gray-400">{message.queryDetails.orderBy.length} sorting rule(s)</span>
                      </div>
                    )}
                    
                    {/* Analysis Complexity Details */}
                    {message.structuredData?.analysisComplexity && (
                      <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                        <div className="space-y-2">
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            <span className="font-medium">Analysis Strategy:</span> {message.structuredData.analysisComplexity.processingStrategy}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            <span className="font-medium">Complexity Level:</span> {message.structuredData.analysisComplexity.description}
                          </div>
                          {message.structuredData.analysisComplexity.aiEnhanced && (
                            <div className="text-xs text-blue-600 dark:text-blue-400">
                              <span className="font-medium">AI Enhancement:</span> Advanced relationship analysis applied
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Processing Info */}
                    {(message.queryDetails.processingTime || message.queryDetails.cached !== undefined) && (
                      <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                          {message.queryDetails.processingTime && (
                            <span>
                              <span className="font-medium">Processing:</span> {message.queryDetails.processingTime}ms
                            </span>
                          )}
                          {message.queryDetails.cached !== undefined && (
                            <span>
                              <span className="font-medium">Cached:</span> {message.queryDetails.cached ? 'Yes' : 'No'}
                            </span>
                          )}
                          {message.queryDetails.aiAnalytics && (
                            <span>
                              <span className="font-medium">Source:</span> AI Analytics Agent
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Follow-up Questions - Mobile Optimized */}
          {(message.followUpQuestions || message.structuredData?.followUpQuestions) && (
            <div className="mb-4 sm:mb-6">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">💭 Suggested follow-up questions</h4>
              <div className="grid grid-cols-1 gap-2 sm:gap-3">
                {(message.followUpQuestions || message.structuredData?.followUpQuestions || []).map((question: string, index: number) => (
                  <button
                    key={index}
                    onClick={() => onFollowUpQuestion?.(question)}
                    className="text-left p-3 sm:p-4 text-sm bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 mobile-rounded transition-colors text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 min-h-[44px] flex items-center mobile-touch-feedback"
                  >
                    <span className="leading-relaxed">{question}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Event Clarification */}
          {message.metadata?.clarificationType && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Event Clarification</h3>
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                <div className="space-y-4">
                  {/* Status indicator */}
                  {message.structuredData?.hasRealEvents && (
                    <div className="flex items-center space-x-2 text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded-lg">
                      <div className="w-2 h-2 bg-green-500 dark:bg-green-400 rounded-full"></div>
                      <span>Found {message.structuredData.totalEventsFound} relevant events from your GA4 setup</span>
                    </div>
                  )}

                  {/* Suggested Events */}
                  {message.structuredData?.suggestedEvents && message.structuredData.suggestedEvents.length > 0 && (
                    <div>
                      <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">
                        {message.structuredData.hasRealEvents ? 'Events from Your GA4 Setup' : 'Suggested Events'}
                      </h4>
                      <div className="space-y-3">
                        {message.structuredData.suggestedEvents.map((event, index) => (
                          <div key={index} className={`p-3 rounded-lg border ${
                            event.isFromGA4 
                              ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700/50' 
                              : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
                          }`}>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <code className="text-sm font-mono bg-white dark:bg-gray-900 dark:text-gray-100 px-2 py-1 rounded border dark:border-gray-600">
                                    {event.eventName}
                                  </code>
                                                                     {event.isFromGA4 && (
                                     <span className="text-xs bg-blue-100 dark:bg-blue-800/50 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full">
                                       Real Event
                                     </span>
                                   )}
                                   {(event as any).source === 'synonym' && (
                                     <span className="text-xs bg-purple-100 dark:bg-purple-800/50 text-purple-700 dark:text-purple-300 px-2 py-1 rounded-full">
                                       Synonym
                                     </span>
                                   )}
                                   {event.similarity && event.similarity > 0 && (
                                     <span className="text-xs bg-green-100 dark:bg-green-800/50 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
                                       {Math.round(event.similarity * 100)}% match
                                     </span>
                                   )}
                                </div>
                                                                 <p className="text-gray-700 dark:text-gray-300 mt-1 text-sm">{event.description}</p>
                                 {(event as any).synonym && (
                                   <p className="text-purple-600 dark:text-purple-400 mt-1 text-xs">
                                     Synonym: "{(event as any).synonym}"
                                   </p>
                                 )}
                                 {event.example && (
                                   <p className="text-gray-500 dark:text-gray-400 mt-1 text-xs italic">{event.example}</p>
                                 )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Help Text */}
                  {message.structuredData?.helpText && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5">💡</div>
                        <p className="text-amber-800 dark:text-amber-200 text-sm leading-relaxed">{message.structuredData.helpText}</p>
                      </div>
                    </div>
                  )}

                  {/* Quick action buttons for common events */}
                  {message.structuredData?.suggestedEvents && message.structuredData.suggestedEvents.length > 0 && (
                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Quick select:</p>
                      <div className="flex flex-wrap gap-2">
                        {message.structuredData.suggestedEvents.slice(0, 3).map((event, index) => (
                          <button
                            key={index}
                            onClick={() => onFollowUpQuestion?.(`Show me users who triggered the "${event.eventName}" event last month`)}
                            className="text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-gray-700 dark:text-gray-300 px-3 py-1 rounded-full transition-colors"
                          >
                            Use "{event.eventName}"
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Playbook */}
          {structuredData?.playbook && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">📖 Playbook</h3>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-lg p-6 border border-blue-200 dark:border-blue-700">
                <div className="space-y-6">
                  {/* Title */}
                  {structuredData.playbook.title && (
                    <div>
                      <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{structuredData.playbook.title}</h4>
                    </div>
                  )}

                  {/* Summary */}
                  {structuredData.playbook.summary && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                      <h5 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-2">Summary</h5>
                      <p className="text-gray-800 dark:text-gray-200 leading-relaxed">{structuredData.playbook.summary}</p>
                    </div>
                  )}

                  {/* Steps */}
                  {structuredData.playbook.steps && structuredData.playbook.steps.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                      <h5 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-3">📋 Implementation Steps</h5>
                      <div className="space-y-4">
                        {structuredData.playbook.steps.map((step, index) => (
                          <div key={index} className="border-l-4 border-blue-400 dark:border-blue-500 pl-4">
                            <div className="flex items-start space-x-3">
                              <div className="w-6 h-6 bg-blue-500 dark:bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                                {index + 1}
                              </div>
                              <div className="flex-1">
                                <h6 className="font-semibold text-gray-900 dark:text-white mb-1">{step.title}</h6>
                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">{step.description}</p>
                                {step.code && (
                                  <div className="bg-gray-900 dark:bg-gray-950 text-gray-100 dark:text-gray-200 p-3 rounded text-sm font-mono overflow-x-auto">
                                    {step.code}
                                  </div>
                                )}
                                {step.tips && step.tips.length > 0 && (
                                  <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded">
                                    <p className="text-xs font-medium text-yellow-800 dark:text-yellow-200 mb-1">💡 Tips:</p>
                                    <ul className="text-xs text-yellow-700 dark:text-yellow-300 space-y-1">
                                      {step.tips.map((tip, tipIndex) => (
                                        <li key={tipIndex} className="flex items-start">
                                          <span className="mr-1">•</span>
                                          <span>{tip}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Best Practices */}
                  {structuredData.playbook.bestPractices && structuredData.playbook.bestPractices.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-green-200 dark:border-green-700">
                      <h5 className="text-sm font-semibold text-green-700 dark:text-green-300 mb-3">✅ Best Practices</h5>
                      <div className="space-y-2">
                        {structuredData.playbook.bestPractices.map((practice, index) => (
                          <div key={index} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-green-500 dark:bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                            <p className="text-green-800 dark:text-green-200 leading-relaxed">{practice}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Common Mistakes */}
                  {structuredData.playbook.commonMistakes && structuredData.playbook.commonMistakes.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-red-200 dark:border-red-700">
                      <h5 className="text-sm font-semibold text-red-700 dark:text-red-300 mb-3">⚠️ Common Mistakes to Avoid</h5>
                      <div className="space-y-2">
                        {structuredData.playbook.commonMistakes.map((mistake, index) => (
                          <div key={index} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-red-500 dark:bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                            <p className="text-red-800 dark:text-red-200 leading-relaxed">{mistake}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Next Steps */}
                  {structuredData.playbook.nextSteps && structuredData.playbook.nextSteps.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-yellow-200 dark:border-yellow-700">
                      <h5 className="text-sm font-semibold text-yellow-700 dark:text-yellow-300 mb-3">🚀 Next Steps</h5>
                      <div className="space-y-2">
                        {structuredData.playbook.nextSteps.map((step, index) => (
                          <div key={index} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-yellow-500 dark:bg-yellow-400 rounded-full mt-2 flex-shrink-0"></div>
                            <p className="text-gray-800 dark:text-gray-200 leading-relaxed">{step}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Resources */}
                  {structuredData.playbook.resources && structuredData.playbook.resources.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
                      <h5 className="text-sm font-semibold text-purple-700 dark:text-purple-300 mb-3">📚 Additional Resources</h5>
                      <div className="space-y-3">
                        {structuredData.playbook.resources.map((resource, index) => (
                          <div key={index} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-purple-500 dark:bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                            <div>
                              <div className="flex items-center space-x-2">
                                <span className="font-medium text-gray-900 dark:text-white">{resource.title}</span>
                                <a 
                                  href={resource.url} 
                                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline text-sm" 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                >
                                  🔗 Link
                                </a>
                              </div>
                              <p className="text-gray-700 dark:text-gray-300 text-sm mt-1">{resource.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Message Actions - Mobile Optimized */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-3 sm:px-6 py-3 border-t border-gray-100 dark:border-gray-700 space-y-3 sm:space-y-0">
          <div className="flex items-center space-x-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleCopy} 
              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 h-9 px-3 mobile-rounded mobile-touch-feedback"
            >
              <Copy className="w-4 h-4 mr-2" />
              <span className="text-sm">Copy</span>
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 h-9 px-3 mobile-rounded mobile-touch-feedback"
            >
              <ThumbsUp className="w-4 h-4 mr-2" />
              <span className="text-sm hidden sm:inline">Helpful</span>
              <span className="text-sm sm:hidden">👍</span>
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 h-9 px-3 mobile-rounded mobile-touch-feedback"
            >
              <ThumbsDown className="w-4 h-4 mr-2" />
              <span className="text-sm hidden sm:inline">Not helpful</span>
              <span className="text-sm sm:hidden">👎</span>
            </Button>
          </div>
          
          <div className="text-xs text-gray-500 dark:text-gray-400 text-center sm:text-right">
            <span className="hidden sm:inline">Fresh data • Not cached</span>
            <span className="sm:hidden">Fresh data</span>
          </div>
        </div>
      </div>
    </div>
  );
} 